import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    Check,
    CheckCheck,
    Trash2,
    X,
    Users,
    CheckSquare,
    AlertTriangle,
    ClipboardList,
    FileText,
    Clock,
    ChevronRight,
    MessageSquare
} from 'lucide-react';
import { useAdminNotifications } from '../../context/AdminNotificationContext';

const NotificationDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const {
        notifications,
        unreadCount,
        loading,
        markAsRead,
        markAllAsRead,
        dismissNotification,
        dismissAllNotifications,
        refreshNotifications
    } = useAdminNotifications();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Refresh notifications when dropdown opens
    useEffect(() => {
        if (isOpen) {
            refreshNotifications();
        }
    }, [isOpen]);

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'employee_added':
                return <Users size={16} className="text-blue-500" />;
            case 'task_completed':
                return <CheckSquare size={16} className="text-green-500" />;
            case 'task_overdue':
                return <AlertTriangle size={16} className="text-red-500" />;
            case 'compliance_violation':
                return <AlertTriangle size={16} className="text-orange-500" />;
            case 'survey_completed':
                return <ClipboardList size={16} className="text-purple-500" />;
            case 'task_assigned':
                return <FileText size={16} className="text-blue-500" />;
            case 'message_received':
                return <MessageSquare size={16} className="text-primary" />;
            default:
                return <Bell size={16} className="text-gray-500" />;
        }
    };

    const getNotificationBg = (type) => {
        switch (type) {
            case 'employee_added':
                return 'bg-blue-50';
            case 'task_completed':
                return 'bg-green-50';
            case 'task_overdue':
                return 'bg-red-50';
            case 'compliance_violation':
                return 'bg-orange-50';
            case 'survey_completed':
                return 'bg-purple-50';
            case 'message_received':
                return 'bg-primary/10';
            default:
                return 'bg-gray-50';
        }
    };

    const formatTimeAgo = (date) => {
        const now = new Date();
        const created = new Date(date);
        const diffMs = now - created;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;
        return created.toLocaleDateString();
    };

    const handleNotificationClick = (notification) => {
        // Mark as read
        if (!notification.is_read) {
            markAsRead(notification.id);
        }

        // Navigate based on entity type or notification type
        if (notification.notification_type === 'message_received') {
            navigate('/admin/messages');
        } else {
            switch (notification.entity_type) {
                case 'employee':
                    navigate('/admin/employees');
                    break;
                case 'task':
                    navigate('/admin/tasks');
                    break;
                case 'survey':
                    navigate('/admin/surveys');
                    break;
                case 'compliance':
                    navigate('/admin/compliance');
                    break;
                case 'message':
                    navigate('/admin/messages');
                    break;
                default:
                    break;
            }
        }

        setIsOpen(false);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
                <Bell size={20} className="text-gray-600" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                        <div className="flex items-center gap-2">
                            <Bell size={18} className="text-gray-600" />
                            <h3 className="font-semibold text-gray-800">Notifications</h3>
                            {unreadCount > 0 && (
                                <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-xs font-medium">
                                    {unreadCount} new
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1">
                            {unreadCount > 0 && (
                                <button
                                    onClick={markAllAsRead}
                                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                                    title="Mark all as read"
                                >
                                    <CheckCheck size={16} className="text-gray-500" />
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <button
                                    onClick={dismissAllNotifications}
                                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                                    title="Clear all"
                                >
                                    <Trash2 size={16} className="text-gray-500" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                        {loading ? (
                            <div className="p-8 text-center">
                                <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto"></div>
                                <p className="text-gray-500 text-sm mt-2">Loading...</p>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="p-8 text-center">
                                <Bell size={32} className="text-gray-300 mx-auto mb-2" />
                                <p className="text-gray-500 text-sm">No notifications</p>
                                <p className="text-gray-400 text-xs mt-1">You're all caught up!</p>
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`
                                        px-4 py-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer
                                        transition-colors group relative
                                        ${!notification.is_read ? 'bg-blue-50/50' : ''}
                                    `}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getNotificationBg(notification.notification_type)}`}>
                                            {getNotificationIcon(notification.notification_type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={`text-sm ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                                    {notification.title}
                                                </p>
                                                {!notification.is_read && (
                                                    <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1.5"></span>
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                                                {notification.message}
                                            </p>
                                            <div className="flex items-center gap-2 mt-1">
                                                <Clock size={12} className="text-gray-400" />
                                                <span className="text-xs text-gray-400">
                                                    {formatTimeAgo(notification.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                dismissNotification(notification.id);
                                            }}
                                            className="p-1 hover:bg-gray-200 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                                            title="Dismiss"
                                        >
                                            <X size={14} className="text-gray-400" />
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
                        <button
                            onClick={() => {
                                navigate('/admin/notifications');
                                setIsOpen(false);
                            }}
                            className="w-full text-center text-sm text-primary hover:text-primary-hover font-medium py-1 flex items-center justify-center gap-1"
                        >
                            View all notifications
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationDropdown;
